import { useState } from 'react'
import { PencilLine, Receipt, RefreshCw, Trash2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useAbandonFollowing } from '@/features/doer/use-doer'
import {
  useMaterialAcquisitions,
  useToolAcquisitions,
} from '@/features/acquisitions/use-acquisitions'
import { DoingOutline } from './doing-outline'
import { NextUpStrip } from './next-up-strip'
import { PersonalizedBomPanel } from './personalized-bom-panel'
import { RefreshSnapshotDialog } from './refresh-snapshot-dialog'
import { DriftStrip } from '@/components/acquisitions/drift-strip'
import { computeDriftSummary, formatUsd, rollupBomFromSteps } from '@/features/projects/rollup-bom'
import { computeStepReadiness } from '@/features/projects/step-readiness'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import { useMemo } from 'react'
import type { FollowingDetail } from '@/api/types'

interface DoingViewProps {
  following: FollowingDetail
  /**
   * True iff the signed-in user is also the source project's author. Authors who started their own
   * project see an "Edit project" toggle that flips to {@code ?mode=edit} without abandoning the
   * Following.
   */
  viewerIsAuthor?: boolean
}

const MobilePersonalizedBomTrigger = ({ following }: { following: FollowingDetail }) => {
  const rollup = rollupBomFromSteps(following.snapshotSteps, following.catalog, following.inventory)
  const isEmpty = rollup.materials.length === 0 && rollup.tools.length === 0
  const total = isEmpty
    ? 'Bill of materials'
    : `Still to buy · ${formatUsd(rollup.grandTotalCents)}`
  return (
    <div className="lg:hidden">
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="default"
            className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2 gap-2 shadow-lg"
          >
            <Receipt className="h-4 w-4" />
            {total}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom">
          <SheetHeader>
            <SheetTitle>Personalized BOM</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <PersonalizedBomPanel following={following} variant="plain" />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

const DoingHeader = ({
  following,
  viewerIsAuthor,
}: {
  following: FollowingDetail
  viewerIsAuthor: boolean
}) => {
  const navigate = useNavigate()
  const abandon = useAbandonFollowing()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const outOfDate = following.currentSourceVersion > following.sourceVersion

  const handleAbandon = (): void => {
    abandon.mutate(following.projectId, {
      onSuccess: () => {
        toast.success('Project abandoned')
        navigate('/dashboard')
      },
      onError: () => toast.error('Could not abandon project'),
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {following.sourceTitle}
            </h1>
            <Badge variant="secondary" className="text-xs">
              Doing
            </Badge>
            {outOfDate && (
              <Badge variant="outline" className="text-xs">
                Out of date
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            By @{following.sourceAuthor.handle} · started{' '}
            {new Date(following.startedAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {viewerIsAuthor && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() =>
                navigate(
                  canonicalProjectUrl(
                    following.sourceAuthor.handle,
                    following.sourceSlug,
                    '?mode=edit'
                  )
                )
              }
            >
              <PencilLine className="h-4 w-4" />
              Edit project
            </Button>
          )}
          <RefreshSnapshotDialog
            projectId={following.projectId}
            outOfDate={outOfDate}
            trigger={
              <Button variant="outline" size="sm" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            }
          />
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 text-destructive">
                <Trash2 className="h-4 w-4" />
                Abandon
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Abandon this project?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your snapshot and progress will be deleted. The source project itself is
                  unaffected, and you can start it again later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleAbandon}>Abandon</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {following.sourceDescription && (
        <p className="whitespace-pre-line text-sm text-muted-foreground">
          {following.sourceDescription}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Need to add what you already own?{' '}
        <Link to="/me/inventory" className="underline">
          Manage inventory
        </Link>
      </p>
    </div>
  )
}

/**
 * Phase 12: doing-view drift strip. Renders nothing when starting BOM total wasn't captured
 * (pre-Phase-12 followings). Mounts above the main outline + BOM grid.
 */
const DoingDriftStrip = ({ following }: { following: FollowingDetail }) => {
  const materialAcqs = useMaterialAcquisitions(following.projectId)
  const toolAcqs = useToolAcquisitions(following.projectId)
  if (following.startingBomTotalCents == null) return null
  const drift = computeDriftSummary(
    following.snapshotSteps,
    following.catalog,
    following.inventory,
    materialAcqs.data ?? [],
    toolAcqs.data ?? [],
    following.startingBomTotalCents
  )
  return <DriftStrip drift={drift} />
}

/**
 * Doing-view scaffold. Mobile-first column layout; on lg+ the personalized BOM moves to a side
 * panel and the floating mobile sheet hides.
 */
export const DoingView = ({ following, viewerIsAuthor = false }: DoingViewProps) => {
  const completedStepIds = useMemo(
    () => new Set(following.progress.map((p) => p.snapshotStepId)),
    [following.progress]
  )
  const readinessMap = useMemo(
    () => computeStepReadiness(following.snapshotSteps, completedStepIds),
    [following.snapshotSteps, completedStepIds]
  )
  return (
    <div className="space-y-6 pb-24 lg:pb-6">
      <DoingHeader following={following} viewerIsAuthor={viewerIsAuthor} />
      <NextUpStrip
        snapshotSteps={following.snapshotSteps}
        completedStepIds={completedStepIds}
        readinessMap={readinessMap}
      />
      <DoingDriftStrip following={following} />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <DoingOutline following={following} />
        </div>
        <div className="hidden space-y-4 lg:block">
          <PersonalizedBomPanel following={following} />
        </div>
      </div>
      <MobilePersonalizedBomTrigger following={following} />
    </div>
  )
}
